// Smart-routing helper for the token-detail "Swap" button (Home v2 / mobile-web).
// Classifies a token against the LibertySwap registry so the dashboard can:
//   'input'       → open Swap & Bridge with the token pre-filled as the FROM side
//   'output'      → open Swap & Bridge with the token pre-filled as the TO side
//   'unsupported' → disable the Swap button (the token can't be swapped/bridged)
//
// Public and private (RAILGUN) modes have different eligible sets:
//   - Public swap sources = LIBERTY_INPUT_TOKEN_IDS (the full from-picker list).
//   - Private swap sources = RAILGUN_INPUT_TOKEN_IDS (the 5 shielded inputs).
import {
  getAvailableToTokens,
  getLibertyInputTokens,
  getLibertyTokenByAddressAndChain,
  isRailgunInputTokenSupported,
  LIBERTY_INPUT_TOKEN_IDS
} from '@ambire-common/services/libertySwap/constants'

export type SwapRole = 'input' | 'output' | 'unsupported'

// Every LibertySwap token id that is a valid public swap *destination*. Built once
// by walking each supported input token's destination list — this is the union of
// everything reachable as a receive token, so a portfolio token that's only ever an
// output (e.g. receive-only CCTP USDC) still resolves to 'output'. Keyed by liberty
// token id (not address) so native tokens match regardless of 0x0/0xEeee form.
const PUBLIC_OUTPUT_TOKEN_IDS: Set<string> = (() => {
  const ids = new Set<string>()
  getLibertyInputTokens().forEach((input) => {
    getAvailableToTokens(input.chainId, 0, input.symbol, input.id, input.chainId).forEach((dest) =>
      ids.add(dest.id)
    )
  })
  return ids
})()

/** Public-mode swap eligibility for a portfolio token. */
export function getPublicSwapRole(address: string, chainId: bigint | number): SwapRole {
  const id = getLibertyTokenByAddressAndChain(address, Number(chainId))?.id
  if (!id) return 'unsupported'
  if (LIBERTY_INPUT_TOKEN_IDS.includes(id)) return 'input'
  if (PUBLIC_OUTPUT_TOKEN_IDS.has(id)) return 'output'
  return 'unsupported'
}

/**
 * Private-mode (RAILGUN) swap eligibility. Only the 5 whitelisted shielded inputs
 * can be a private swap source; nothing is a private "output" from the token detail
 * (the receive side is PulseChain-only and chosen inside the swap form).
 */
export function getPrivateSwapRole(address: string, chainId: bigint | number): SwapRole {
  return isRailgunInputTokenSupported(address, Number(chainId)) ? 'input' : 'unsupported'
}

/** Resolve swap eligibility for the given app mode. */
export function getSwapRole(
  address: string,
  chainId: bigint | number,
  isPrivate: boolean
): SwapRole {
  return isPrivate ? getPrivateSwapRole(address, chainId) : getPublicSwapRole(address, chainId)
}
